import React from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  DocumentIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';
import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { AutoResizeTextarea } from '@oysterjs/ui/textarea';

import { FileInput } from '~/components/file';

import {
  createEmptySection,
  type WordTemplateFormState,
  type WordTemplateSectionFormEntry
} from '~/components/library/lib/word-template-sections';

export interface SectionedTemplateFieldsProps {
  form: WordTemplateFormState;
  updateForm: (
    path: string[],
    updater: (prev: WordTemplateFormState) => WordTemplateFormState
  ) => void;
  getValidationError: (path: string[]) => string | undefined;
}

/** Collapsible list of section cards plus an "Add section" button. Used by
 *  both the create dialog (sectioned mode) and the editor. */
export const SectionedTemplateFields = (props: SectionedTemplateFieldsProps) => {
  const sections = props.form.sections;
  const [expandedSections, setExpandedSections] = React.useState<Set<number>>(() => new Set([]));

  const replaceSection = (
    i: number,
    field: string,
    patch: Partial<WordTemplateSectionFormEntry>
  ) => {
    props.updateForm(['sections', String(i), field], (prev) => ({
      ...prev,
      sections: prev.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s))
    }));
  };

  const addSection = () => {
    props.updateForm(['sections'], (prev) => ({
      ...prev,
      sections: [...prev.sections, createEmptySection()]
    }));
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.add(sections.length);
      return next;
    });
  };

  const removeSection = (i: number) => {
    props.updateForm(['sections'], (prev) => ({
      ...prev,
      sections: prev.sections.filter((_, idx) => idx !== i)
    }));
    setExpandedSections((prev) => {
      const next = new Set<number>();
      prev.forEach((idx) => {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      });
      return next;
    });
  };

  const moveSection = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    props.updateForm(['sections'], (prev) => {
      const next = [...prev.sections];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...prev, sections: next };
    });
    setExpandedSections((prev) => {
      const next = new Set(prev);
      const hadI = next.has(i);
      const hadJ = next.has(j);
      if (hadI) next.add(j);
      else next.delete(j);
      if (hadJ) next.add(i);
      else next.delete(i);
      return next;
    });
  };

  const setSectionOpen = (i: number, open: boolean) => {
    setExpandedSections((prev) => {
      if (open && prev.has(i)) return prev;
      if (!open && !prev.has(i)) return prev;
      const next = new Set(prev);
      if (open) next.add(i);
      else next.delete(i);
      return next;
    });
  };

  return (
    <Field>
      <Label>Sections</Label>
      <Description>
        Add one .docx per section. Cara will fill each section in the order shown.
      </Description>
      <div className="space-y-4" data-slot="control">
        {sections.map((section, i) => (
          <SectionCard
            key={i}
            index={i}
            total={sections.length}
            section={section}
            defaultOpen={
              expandedSections.has(i) ||
              props.getValidationError(['sections', String(i), 'title']) !== undefined ||
              props.getValidationError(['sections', String(i), 'uploadTemplate']) !== undefined
            }
            onOpenChange={(open) => setSectionOpen(i, open)}
            onChange={(field, patch) => replaceSection(i, field, patch)}
            onRemove={() => removeSection(i)}
            onMoveUp={() => moveSection(i, -1)}
            onMoveDown={() => moveSection(i, 1)}
            getValidationError={(field) => props.getValidationError(['sections', String(i), field])}
          />
        ))}
        {sections.length === 0 && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            No sections yet. Add one to get started.
          </p>
        )}
        <Button type="button" color="sky" onClick={addSection}>
          Add section
        </Button>
      </div>
    </Field>
  );
};

interface SectionCardProps {
  index: number;
  total: number;
  section: WordTemplateSectionFormEntry;
  defaultOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (field: string, patch: Partial<WordTemplateSectionFormEntry>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  getValidationError: (field: string) => string | undefined;
}

const SectionCard = ({
  index,
  total,
  section,
  defaultOpen,
  onOpenChange,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  getValidationError
}: SectionCardProps) => {
  const titleError = getValidationError('title');
  const fileError = getValidationError('uploadTemplate');
  const hasErrors = !!titleError || !!fileError;

  // Display name shown in the collapsed header summary. Prefer a freshly
  // uploaded file, then the currently stored file (in edit mode), then a
  // placeholder.
  const uploadedFileName = section.uploadTemplate?.name ?? null;
  const existingFileName = section.existingFile?.name ?? null;
  const summaryFileName = uploadedFileName ?? existingFileName ?? 'No file selected';
  const summaryParts: string[] = [summaryFileName];
  if (section.static) summaryParts.push('Static');
  if (section.repeatable) summaryParts.push('Repeatable');
  if (!section.required) summaryParts.push('Optional');
  const summary = summaryParts.join(' · ');

  return (
    <Headless.Disclosure as="div" defaultOpen={defaultOpen}>
      {({ open }) => {
        // Keep the parent's expandedSections in sync so add/remove/move can
        // remap correctly without forcing a remount.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
          onOpenChange(open);
          // We only want to react to local open changes, not parent re-renders.
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [open]);

        return (
          <div className="border border-neutral-200 rounded-lg bg-white dark:bg-neutral-800 dark:border-neutral-700 shadow-sm">
            <Headless.DisclosureButton className="flex w-full items-center justify-between p-4 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors">
              <div className="flex flex-1 items-center gap-3 min-w-0">
                <ChevronDownIcon
                  className={clsx(
                    'h-5 w-5 text-neutral-400 dark:text-neutral-500 transition-transform duration-200 shrink-0',
                    open && 'rotate-180'
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex min-w-0">
                    <span className="block min-w-0 flex-1 truncate text-sm font-medium text-neutral-900 dark:text-white">
                      <span className="inline-flex items-center gap-2">
                        <span>{section.title.trim() || `Section ${index + 1}`}</span>
                        {hasErrors && (
                          <span className="inline-block size-2 rounded-full bg-red-500 align-middle" />
                        )}
                      </span>
                    </span>
                  </div>
                  <div className="block min-w-0 truncate text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {summary}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                <Headless.Button
                  as="button"
                  type="button"
                  aria-label="Move section up"
                  disabled={index === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveUp();
                  }}
                  className="p-0.5 rounded text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <ArrowUpIcon className="size-4" />
                </Headless.Button>
                <Headless.Button
                  as="button"
                  type="button"
                  aria-label="Move section down"
                  disabled={index === total - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveDown();
                  }}
                  className="p-0.5 rounded text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <ArrowDownIcon className="size-4" />
                </Headless.Button>
                <Headless.Button
                  as="button"
                  type="button"
                  aria-label="Remove section"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  className="p-0.5 rounded text-neutral-500 hover:text-red-600 hover:bg-red-50 dark:text-neutral-400 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                >
                  <XMarkIcon className="size-4" />
                </Headless.Button>
              </div>
            </Headless.DisclosureButton>

            <Headless.DisclosurePanel
              unmount={false}
              className="px-4 pb-4 space-y-4 border-t border-neutral-200 dark:border-neutral-700 pt-4"
            >
              <Field>
                <Label>Section title</Label>
                <Input
                  invalid={!!titleError}
                  value={section.title}
                  onChange={(e) => onChange('title', { title: e.currentTarget.value })}
                  placeholder={`e.g. Section ${index + 1}`}
                />
                {titleError && <ErrorMessage>{titleError}</ErrorMessage>}
              </Field>

              <SectionFileField section={section} onChange={onChange} fileError={fileError} />

              <Field>
                <Label>Setup guidance (optional)</Label>
                <Description>
                  Context for Cara when it analyzes this section&apos;s .docx. Use it to clarify how
                  the section should be filled — which placeholders matter, how rows repeat, or
                  which boilerplate must stay verbatim. Applied the next time the template is
                  processed.
                </Description>
                <AutoResizeTextarea
                  value={section.setupGuidance ?? ''}
                  rows={3}
                  resizable={false}
                  onChange={(e) =>
                    onChange('setupGuidance', { setupGuidance: e.currentTarget.value })
                  }
                  placeholder="Example: This table has one row per scheduled vehicle. Keep the carrier disclaimer at the bottom verbatim."
                />
              </Field>

              <SwitchField>
                <Label>Static</Label>
                <Description>
                  This section is fixed text. Cara will copy it through unchanged.
                </Description>
                <Switch
                  color="sky"
                  checked={section.static}
                  onChange={(checked) => onChange('static', { static: checked })}
                />
              </SwitchField>

              <SwitchField>
                <Label>Repeatable</Label>
                <Description>
                  Cara may generate this section more than once per document when needed.
                </Description>
                <Switch
                  color="sky"
                  checked={section.repeatable}
                  onChange={(checked) => onChange('repeatable', { repeatable: checked })}
                />
              </SwitchField>

              <SwitchField>
                <Label>Required</Label>
                <Description>
                  This section must be included in every document. Turn off to let Cara skip it when
                  it isn&apos;t relevant to the request.
                </Description>
                <Switch
                  color="sky"
                  checked={section.required}
                  onChange={(checked) => onChange('required', { required: checked })}
                />
              </SwitchField>
            </Headless.DisclosurePanel>
          </div>
        );
      }}
    </Headless.Disclosure>
  );
};

/** File picker for a single section card. Three rendering modes:
 *
 *  1. No existing file (create flow, or newly added section in edit flow):
 *     show the plain file input.
 *  2. Existing file present, no replacement yet: show the stored file's
 *     name, a download link, and a "Replace file" button (mirrors the
 *     custom-form-edit-dialog UX).
 *  3. Existing file present, replacement chosen: show the file input with
 *     the new file and a "Keep current file" button that restores the
 *     stored file.
 *
 *  The component drives the section input's `uploadTemplate` /
 *  `templateFileId` pair so the server can tell whether the user wants to
 *  keep the existing file (templateFileId set, uploadTemplate null) or
 *  replace it (uploadTemplate set, templateFileId null). */
const SectionFileField = ({
  section,
  onChange,
  fileError
}: {
  section: WordTemplateSectionFormEntry;
  onChange: SectionCardProps['onChange'];
  fileError: string | undefined;
}) => {
  const existingFile = section.existingFile ?? null;
  const hasUpload = !!section.uploadTemplate;
  // Three states for sections that loaded with an existing file:
  //   templateFileId set, no upload    → keep current (stored-file row)
  //   templateFileId cleared, no upload → user clicked Replace (picker, empty)
  //   upload set                       → replacement picked (picker, with file)
  // Sections with no existing file (new uploads) always render the picker.
  const stillPinnedToStoredFile = !!section.templateFileId && !hasUpload;
  const showStoredFileRow = !!existingFile && stillPinnedToStoredFile;

  return (
    <Field>
      <Label>Word file (.docx)</Label>
      <Description>.docx only. Not .doc, .pdf, .pages, or .dotx. One file per section.</Description>
      {showStoredFileRow ? (
        <div data-slot="control" className="mt-2 flex flex-row gap-4 overflow-hidden">
          <div className="flex flex-row gap-2 min-w-0 overflow-hidden">
            <DocumentIcon className="size-10 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
            <div className="flex flex-col min-w-0 overflow-hidden gap-1">
              <div
                className="text-sm text-neutral-700 truncate dark:text-neutral-200"
                title={existingFile.name}
              >
                {existingFile.name}
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:gap-4 sm:items-center">
                {existingFile.url && (
                  <a
                    href={existingFile.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline cursor-pointer shrink-0"
                  >
                    Download file
                  </a>
                )}
                <button
                  type="button"
                  onClick={() =>
                    // Drop the templateFileId so the server doesn't keep the
                    // existing file; once the user picks a replacement,
                    // uploadTemplate will be set in the FileInput onChange
                    // below.
                    onChange('uploadTemplate', { templateFileId: null })
                  }
                  className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline cursor-pointer text-left shrink-0"
                >
                  Replace file
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div data-slot="control" className="mt-2 flex flex-col gap-3">
          <FileInput
            invalid={!!fileError}
            onChange={(change) => {
              const next = change.files[0]?.upload ?? null;
              // Picking a file always switches us into "replace" semantics:
              // clear templateFileId so the server uses the newly uploaded
              // file instead of the stored one.
              onChange('uploadTemplate', { uploadTemplate: next, templateFileId: null });
            }}
          />
          {existingFile && (
            <button
              type="button"
              onClick={() =>
                // Discard any pending replacement and restore the stored
                // file by re-pinning its id.
                onChange('uploadTemplate', {
                  uploadTemplate: null,
                  templateFileId: existingFile.id
                })
              }
              className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline cursor-pointer text-left self-start"
            >
              Keep current file
            </button>
          )}
          {fileError && <ErrorMessage>{fileError}</ErrorMessage>}
        </div>
      )}
    </Field>
  );
};
